import React, { useState, useEffect, FC } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { getLogin } from "Slices/appState";
import { getLoginErrorText } from "Slices/appState/selectors";
import { getCognitoLoginUrl, cognitoConfig } from "Config/cognito";
import { getCognitoRetryCount } from "Utils/cognito";

import {
  SignInFormContainerStyled,
  TitleStyled,
  InputStyled,
  SubmitStyled,
  LoginErrMsgStyled,
  ForgotPasswordStyled,
  CognitoLoginButton,
} from "./styles";

type LoginProps = {
  onForgotSelected: () => void;
};

export const LoginComponent: FC<LoginProps> = ({ onForgotSelected }) => {
  const loginErrorText = useSelector(getLoginErrorText);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoginDataEmpty, setIsLoginDataEmpty] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleCognitoLogin = () => {
    // Redirect to Cognito Hosted UI
    window.location.href = getCognitoLoginUrl();
  };

  // Check if Cognito is configured (also used by auto-retry below)
  const isCognitoConfigured = cognitoConfig.clientId && cognitoConfig.domain;

  // Stale-session auto-retry: if CognitoCallback failed and redirected us
  // through /logout (counter incremented), kick off SSO again immediately
  // so the user sees a single seamless redirect instead of the login screen.
  useEffect(() => {
    if (isCognitoConfigured && getCognitoRetryCount() > 0) {
      console.info(
        "[Cognito] Stale-session recovery: re-initiating SSO after /logout"
      );
      window.location.href = getCognitoLoginUrl();
    }
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsLoginDataEmpty(false); // clear out errors on each submit

    if (!email || !password) {
      setIsLoginDataEmpty(true);
      return;
    }

    dispatch(getLogin({ email, password, navigateCallBack: navigate }));
  };

  return (
    <SignInFormContainerStyled onSubmit={handleSubmit}>
      <TitleStyled>Sign In</TitleStyled>
      {isLoginDataEmpty && (
        <LoginErrMsgStyled>Username and password required.</LoginErrMsgStyled>
      )}
      <InputStyled
        type="email"
        name="email"
        onChange={(e) => setEmail(e.currentTarget.value.toLowerCase())}
        placeholder="Email"
        value={email}
      />
      <InputStyled
        type="password"
        name="password"
        onChange={(e) => setPassword(e.currentTarget.value)}
        placeholder="Password"
        value={password}
      />
      <SubmitStyled>SUBMIT</SubmitStyled>
      {isCognitoConfigured && (
        <CognitoLoginButton type="button" onClick={handleCognitoLogin}>
          Sign in with SSO
        </CognitoLoginButton>
      )}
      <ForgotPasswordStyled onClick={onForgotSelected}>
        FORGOT PASSWORD
      </ForgotPasswordStyled>
      {loginErrorText && (
        <LoginErrMsgStyled>{loginErrorText}</LoginErrMsgStyled>
      )}
    </SignInFormContainerStyled>
  );
};
